import { Point, Size, ViewTransform, apply, multiply, scaling } from './adjustGeometry'

import { AdjustSticker } from './adjust.types'
import { BLUE_600 } from 'constants/styling/theme'
import { stickerCorners } from './adjustStickers'

/** What the canvas shows: the edit, the photo before it, or both split by a draggable line */
export type AdjustViewMode = 'after' | 'before' | 'compare'

/** Screen rectangle of the crop frame (FRAME → SCREEN is axis-aligned) */
export const frameScreenRect = (view: ViewTransform) => {
  const { frame, frameToScreen } = view
  const topLeft = apply(frameToScreen, { x: frame.cx - frame.width / 2, y: frame.cy - frame.height / 2 })
  return { x: topLeft.x, y: topLeft.y, width: frame.width * view.scale, height: frame.height * view.scale }
}

export const compareSplitX = (view: ViewTransform, split: number) => {
  const rect = frameScreenRect(view)
  return rect.x + rect.width * split
}

/** Screen position of a sticker's resize handle (its bottom-right corner) */
export const stickerHandleOnScreen = (view: ViewTransform, sticker: AdjustSticker): Point => apply(view.imageToScreen, stickerCorners(sticker)[2])

export interface DrawViewParams {
  ctx: CanvasRenderingContext2D
  viewport: Size
  pixelRatio: number
  view: ViewTransform
  imageSize: Size
  after: CanvasImageSource | null
  before: CanvasImageSource | null
  mode: AdjustViewMode
  split: number
  /** Crop tool: the whole rotated photo, dimmed outside the frame, with handles */
  showWholePhoto: boolean
  selectedSticker: AdjustSticker | null
  labels: { before: string, after: string }
}

const roundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + width, y, x + width, y + height, radius)
  ctx.arcTo(x + width, y + height, x, y + height, radius)
  ctx.arcTo(x, y + height, x, y, radius)
  ctx.arcTo(x, y, x + width, y, radius)
  ctx.closePath()
}

const drawCropOverlay = (ctx: CanvasRenderingContext2D, viewport: Size, frame: { x: number, y: number, width: number, height: number }) => {
  const { x, y, width, height } = frame
  ctx.save()
  ctx.fillStyle = 'rgba(19, 24, 29, 0.6)'
  ctx.beginPath()
  ctx.rect(0, 0, viewport.width, viewport.height)
  ctx.rect(x, y, width, height)
  ctx.fill('evenodd')

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)'
  ctx.lineWidth = 1
  ctx.beginPath()
  for (let i = 1; i < 3; i++) {
    ctx.moveTo(x + width * i / 3, y)
    ctx.lineTo(x + width * i / 3, y + height)
    ctx.moveTo(x, y + height * i / 3)
    ctx.lineTo(x + width, y + height * i / 3)
  }
  ctx.stroke()

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)'
  ctx.lineWidth = 1.5
  ctx.strokeRect(x, y, width, height)

  // Corner brackets and edge bars: the parts you can drag
  const arm = Math.min(18, width / 4, height / 4)
  ctx.strokeStyle = '#FFFFFF'
  ctx.lineWidth = 4
  ctx.lineCap = 'round'
  ctx.beginPath()
  for (const [cx, cy, dx, dy] of [[x, y, 1, 1], [x + width, y, -1, 1], [x, y + height, 1, -1], [x + width, y + height, -1, -1]]) {
    ctx.moveTo(cx + arm * dx, cy)
    ctx.lineTo(cx, cy)
    ctx.lineTo(cx, cy + arm * dy)
  }
  const bar = Math.min(12, width / 6, height / 6)
  ctx.moveTo(x + width / 2 - bar, y)
  ctx.lineTo(x + width / 2 + bar, y)
  ctx.moveTo(x + width / 2 - bar, y + height)
  ctx.lineTo(x + width / 2 + bar, y + height)
  ctx.moveTo(x, y + height / 2 - bar)
  ctx.lineTo(x, y + height / 2 + bar)
  ctx.moveTo(x + width, y + height / 2 - bar)
  ctx.lineTo(x + width, y + height / 2 + bar)
  ctx.stroke()
  ctx.restore()
}

const drawCompareDivider = (ctx: CanvasRenderingContext2D, frame: { x: number, y: number, width: number, height: number }, split: number, labels: { before: string, after: string }) => {
  const x = frame.x + frame.width * split
  const cy = frame.y + frame.height / 2
  ctx.save()
  ctx.strokeStyle = '#FFFFFF'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(x, frame.y)
  ctx.lineTo(x, frame.y + frame.height)
  ctx.stroke()
  ctx.fillStyle = '#FFFFFF'
  ctx.beginPath()
  ctx.arc(x, cy, 16, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#13181D'
  ctx.beginPath()
  ctx.moveTo(x - 9, cy)
  ctx.lineTo(x - 3, cy - 5)
  ctx.lineTo(x - 3, cy + 5)
  ctx.closePath()
  ctx.moveTo(x + 9, cy)
  ctx.lineTo(x + 3, cy - 5)
  ctx.lineTo(x + 3, cy + 5)
  ctx.closePath()
  ctx.fill()

  ctx.font = '500 12px "Inter Variable", system-ui, sans-serif'
  ctx.textBaseline = 'middle'
  for (const [text, isLeft] of [[labels.before, true], [labels.after, false]] as const) {
    const width = ctx.measureText(text).width + 16
    const left = isLeft ? frame.x + 10 : frame.x + frame.width - 10 - width
    ctx.fillStyle = 'rgba(19, 24, 29, 0.72)'
    roundedRect(ctx, left, frame.y + 10, width, 24, 12)
    ctx.fill()
    ctx.fillStyle = '#FFFFFF'
    ctx.textAlign = 'left'
    ctx.fillText(text, left + 8, frame.y + 22)
  }
  ctx.restore()
}

const drawStickerSelection = (ctx: CanvasRenderingContext2D, view: ViewTransform, sticker: AdjustSticker) => {
  const corners = stickerCorners(sticker).map(p => apply(view.imageToScreen, p))
  ctx.save()
  ctx.strokeStyle = BLUE_600
  ctx.lineWidth = 2
  ctx.setLineDash([6, 4])
  ctx.beginPath()
  ctx.moveTo(corners[0].x, corners[0].y)
  for (let i = 1; i < 4; i++) ctx.lineTo(corners[i].x, corners[i].y)
  ctx.closePath()
  ctx.stroke()
  ctx.setLineDash([])
  ctx.fillStyle = '#FFFFFF'
  ctx.beginPath()
  ctx.arc(corners[2].x, corners[2].y, 8, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()
  ctx.restore()
}

/** Paints the canvas: the photo through the view transform, then the tool's overlay */
export const drawAdjustView = ({ ctx, viewport, pixelRatio, view, imageSize, after, before, mode, split, showWholePhoto, selectedSticker, labels }: DrawViewParams): void => {
  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  ctx.clearRect(0, 0, viewport.width, viewport.height)
  const frame = frameScreenRect(view)

  const drawPhoto = (source: CanvasImageSource) => {
    const m = multiply(scaling(pixelRatio), view.imageToScreen)
    ctx.save()
    ctx.setTransform(m.a, m.b, m.c, m.d, m.e, m.f)
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(source, 0, 0, imageSize.width, imageSize.height)
    ctx.restore()
  }

  ctx.save()
  if (!showWholePhoto) {
    ctx.beginPath()
    ctx.rect(frame.x, frame.y, frame.width, frame.height)
    ctx.clip()
  }
  const shown = mode === 'before' ? before ?? after : after
  if (shown) drawPhoto(shown)
  if (mode === 'compare' && before) {
    ctx.beginPath()
    ctx.rect(frame.x, frame.y, frame.width * split, frame.height)
    ctx.clip()
    drawPhoto(before)
  }
  ctx.restore()

  if (showWholePhoto) drawCropOverlay(ctx, viewport, frame)
  if (mode === 'compare') drawCompareDivider(ctx, frame, split, labels)
  if (mode === 'after' && selectedSticker) drawStickerSelection(ctx, view, selectedSticker)
}
